import { formatNumber } from '@/lib/utils';

export interface BarData {
  label: string;
  value: number;
  tone?: 'default' | 'warning' | 'critical';
  highlight?: boolean;
}

interface Props {
  data: BarData[];
  height?: number;
  maxBars?: number;
  showValues?: boolean;
}

export function BarChart({ data, height = 200, maxBars, showValues = true }: Props) {
  const slice = maxBars ? data.slice(0, maxBars) : data;
  const max = Math.max(1, ...slice.map((d) => d.value));

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-end',
        gap: 6,
        height: height + 36,
        padding: '8px 0',
      }}
    >
      {slice.map((d, i) => {
        const h = d.value > 0 ? (d.value / max) * height : 4;
        const tone =
          d.tone === 'critical'
            ? '#b91c1c'
            : d.tone === 'warning'
              ? '#d97706'
              : 'var(--bx-primary)';
        const bg =
          d.tone === 'critical'
            ? 'linear-gradient(180deg, #fecaca 0%, #fee2e2 100%)'
            : d.tone === 'warning'
              ? 'linear-gradient(180deg, #fde68a 0%, #fefce8 100%)'
              : 'linear-gradient(180deg, #c7d2fe 0%, #eef2ff 100%)';
        return (
          <div
            key={i}
            style={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 4,
              minWidth: 0,
            }}
          >
            {showValues && (
              <div
                style={{
                  fontSize: 10,
                  fontFamily: 'var(--bx-font-mono)',
                  fontWeight: 700,
                  color: tone,
                  whiteSpace: 'nowrap',
                }}
              >
                {d.value > 0 ? formatNumber(d.value) : '—'}
              </div>
            )}
            <div
              style={{
                width: '100%',
                maxWidth: 48,
                height: h,
                background: d.value > 0 ? bg : 'var(--bx-surface-2)',
                border: `1.5px solid ${d.value > 0 ? tone : 'var(--bx-border-muted)'}`,
                borderRadius: 6,
                position: 'relative',
              }}
            >
              {d.highlight && (
                <div
                  style={{
                    position: 'absolute',
                    top: -8,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: 8,
                    height: 8,
                    borderRadius: 9999,
                    background: 'var(--bx-primary)',
                    border: '2px solid #fff',
                  }}
                />
              )}
            </div>
            <div
              style={{
                fontSize: 10.5,
                fontWeight: d.highlight ? 700 : 500,
                color: d.highlight ? 'var(--bx-primary)' : 'var(--bx-text-muted)',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                maxWidth: '100%',
              }}
            >
              {d.label}
            </div>
          </div>
        );
      })}
    </div>
  );
}
